import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
} from '@leagueplatform/analytics';
import { useIntl } from '@leagueplatform/locales';
import type {
  UserMenuItem,
  UserMenuSection,
} from '@leagueplatform/solutions-foundation';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import {
  ANALYTICS_DETAILS,
  ANALYTICS_PRODUCT_AREA_NAMES,
  QUERY_PARAM_MODAL_VALUE,
} from 'common/constants';
import { isPatientPortalDeveloperMode } from 'common/env';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import { useExternalLinks } from 'hooks/use-external-links/use-external-links.hook';
import { useUrlParam } from 'hooks/use-url-param/use-url-param.hook';
import { PATIENT_PORTAL_SYSTEM_IDS } from 'types/patient-portal-system.types';

const openInNewTab = (url: string) =>
  window.open(url, '_blank', 'noopener,noreferrer');

const itemCss = { color: '$onSurfaceTextPrimary' } as const;

const getUserSettingsLinksAnalyticsPayload = () => ({
  eventName: EVENT_NAME.BUTTON_CLICKED,
  eventProps: {
    product_area: ANALYTICS_PRODUCT_AREA_NAMES.FOUNDATION,
    sub_product_area: SCREEN_NAMES.SETTINGS,
    screen_name: SCREEN_NAMES.SETTINGS,
    detail: ANALYTICS_DETAILS.USER_SETTINGS,
  },
});

export const useUserSettingsLinksConfig = () => {
  const { formatMessage } = useIntl();
  const { setParam } = useUrlParam('modal');
  const { systemId, setSystemId } = usePatientPortalContext();
  const externalLinks = useExternalLinks();

  const isSystemIdAmplar = systemId === PATIENT_PORTAL_SYSTEM_IDS.AMPLAR;

  const userMenu: UserMenuSection[] = [
    {
      heading: formatMessage({ id: 'STR_ACCOUNT' }),
      items: [
        {
          label: formatMessage({ id: 'STR_PERSONAL_INFORMATION' }),
          to: APP_MANAGED_ROUTES.PERSONAL_INFORMATION,
          css: itemCss,
          analyticsPayload: getUserSettingsLinksAnalyticsPayload(),
        },
        ...(!isSystemIdAmplar
          ? ([
              {
                label: formatMessage({ id: 'STR_YOUR_CENTRES' }),
                to: APP_MANAGED_ROUTES.LINKED_CENTRES,
                css: itemCss,
                analyticsPayload: getUserSettingsLinksAnalyticsPayload(),
              },
            ] as UserMenuItem[])
          : []),
      ],
    },
    {
      heading: formatMessage({ id: 'STR_GET_SUPPORT' }),
      items: [
        ...(externalLinks.FAQ
          ? ([
              {
                label: formatMessage({ id: 'STR_FAQ' }),
                onClick: () => openInNewTab(externalLinks.FAQ),
                css: itemCss,
                analyticsPayload: getUserSettingsLinksAnalyticsPayload(),
              },
            ] as UserMenuItem[])
          : []),
      ],
    },
    {
      heading: formatMessage({ id: 'STR_ABOUT' }),
      items: [
        {
          label: formatMessage({ id: 'STR_CONTACT_US' }),
          to: APP_MANAGED_ROUTES.CONTACT_US,
          css: itemCss,
          analyticsPayload: {
            eventName: EVENT_NAME.BUTTON_CLICKED,
            eventProps: {
              product_area: ANALYTICS_PRODUCT_AREA_NAMES.FOUNDATION,
              sub_product_area: SCREEN_NAMES.SETTINGS,
              screen_name: SCREEN_NAMES.SETTINGS,
              detail: ANALYTICS_DETAILS.USER_SETTINGS_CONTACT_US,
            },
          },
        },
        ...(!isSystemIdAmplar && externalLinks.PRIVACY_POLICY
          ? ([
              {
                label: formatMessage({ id: 'STR_PRIVACY_POLICY' }),
                onClick: () => openInNewTab(externalLinks.PRIVACY_POLICY),
                css: itemCss,
                analyticsPayload: getUserSettingsLinksAnalyticsPayload(),
              },
            ] as UserMenuItem[])
          : []),
        ...(isSystemIdAmplar && externalLinks.PRIVACY_COLLECTION_NOTICE
          ? ([
              {
                label: formatMessage({ id: 'STR_COLLECTION_NOTICE' }),
                onClick: () =>
                  openInNewTab(externalLinks.PRIVACY_COLLECTION_NOTICE),
                css: itemCss,
                analyticsPayload: getUserSettingsLinksAnalyticsPayload(),
              },
            ] as UserMenuItem[])
          : []),
        ...(externalLinks.TERMS_OF_SERVICE
          ? ([
              {
                label: formatMessage({ id: 'STR_TOS' }),
                onClick: () => openInNewTab(externalLinks.TERMS_OF_SERVICE),
                css: itemCss,
                analyticsPayload: getUserSettingsLinksAnalyticsPayload(),
              },
            ] as UserMenuItem[])
          : []),
        {
          label: formatMessage({ id: 'STR_ACCOUNT_DELETE' }),
          onClick: () => {
            setParam(QUERY_PARAM_MODAL_VALUE.DELETE_ACCOUNT);
          },
          css: itemCss,
          analyticsPayload: getUserSettingsLinksAnalyticsPayload(),
        },
      ],
    },
    ...(isPatientPortalDeveloperMode()
      ? ([
          {
            heading: formatMessage({ id: 'STR_DEVELOPER' }),
            items: [
              {
                label: formatMessage({ id: 'STR_SYSTEM_ID' }, { id: systemId }),
                onClick: () => {
                  setSystemId(
                    isSystemIdAmplar
                      ? PATIENT_PORTAL_SYSTEM_IDS.MY_HEALTH
                      : PATIENT_PORTAL_SYSTEM_IDS.AMPLAR,
                  );
                },
                css: itemCss,
                analyticsPayload: {},
              },
            ],
          },
        ] as UserMenuSection[])
      : []),
    {
      items: [
        {
          label: formatMessage({ id: 'STR_SIGN_OUT' }),
          to: APP_MANAGED_ROUTES.SIGN_OUT,
          css: itemCss,
          analyticsPayload: {
            eventName: EVENT_NAME.BUTTON_CLICKED,
            eventProps: {
              product_area: PRODUCT_AREA.GET_CARE,
              sub_product_area: SCREEN_NAMES.SETTINGS,
              screen_name: SCREEN_NAMES.SETTINGS,
              detail: ANALYTICS_DETAILS.USER_SETTINGS_SIGN_OUT,
            },
          },
        },
      ],
    },
  ];

  return { userMenu };
};
